import { FC } from 'react';
import { Divider } from 'antd';
import { TbBuilding, TbBuildingCommunity, TbChartLine, TbCube, TbLayersIntersect } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FundIcon, type SearchFilterChangeHandler } from '@/shared';
import SearchFilterCheckboxList, {
    type SearchFilterCheckboxOption,
} from '@/shared/components/filters/SearchFilterCheckboxList';
import {
    COMPANY_ASSET_TYPE_OPTIONS,
    COMPANY_ASSET_TYPE_VALUES,
    LEGACY_COMPANY_ASSET_TYPE_ALIASES,
} from '@/shared/constants/companyAssetTypes';
import { ImportedCompanyEntityType } from '@/types';

interface AssetTypeProps {
    filters: Record<string, unknown>;
    handleChange: SearchFilterChangeHandler;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeAssetTypes = (value: unknown): string[] => {
    const selectedValues = Array.isArray(value)
        ? value
        : isRecord(value)
          ? [
                ...(Array.isArray(value.public) ? value.public : []),
                ...(Array.isArray(value.private) ? value.private : []),
            ]
          : [];

    return selectedValues
        .filter((selectedValue): selectedValue is string => typeof selectedValue === 'string')
        .map((selectedValue) => LEGACY_COMPANY_ASSET_TYPE_ALIASES[selectedValue] ?? selectedValue);
};

const AssetClass: FC<AssetTypeProps> = ({ filters, handleChange }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const value = normalizeAssetTypes(filters.assetClasses);
    const iconStyle = { color: token.colorTextTertiary };
    const publicOptions: SearchFilterCheckboxOption[] = [
        {
            icon: <TbBuilding aria-hidden size='1rem' style={iconStyle} />,
            label: 'Companies',
            value: COMPANY_ASSET_TYPE_VALUES.public.company,
        },
        {
            icon: <FundIcon aria-hidden size='1rem' style={iconStyle} />,
            label: 'Funds',
            value: COMPANY_ASSET_TYPE_VALUES.public.fund,
        },
        {
            icon: <TbChartLine aria-hidden size='1rem' style={iconStyle} />,
            label: 'Indices',
            value: COMPANY_ASSET_TYPE_VALUES.public.index,
        },
    ];
    const privateOptionIcons = {
        [ImportedCompanyEntityType.COMPANY]: <TbBuildingCommunity aria-hidden size='1rem' style={iconStyle} />,
        [ImportedCompanyEntityType.FUND]: <FundIcon aria-hidden size='1rem' style={iconStyle} />,
        [ImportedCompanyEntityType.ENTITY]: <TbLayersIntersect aria-hidden size='1rem' style={iconStyle} />,
        [ImportedCompanyEntityType.OTHER]: <TbCube aria-hidden size='1rem' style={iconStyle} />,
    };
    const privateOptionValues = {
        [ImportedCompanyEntityType.COMPANY]: COMPANY_ASSET_TYPE_VALUES.private.company,
        [ImportedCompanyEntityType.FUND]: COMPANY_ASSET_TYPE_VALUES.private.fund,
        [ImportedCompanyEntityType.ENTITY]: COMPANY_ASSET_TYPE_VALUES.private.entity,
        [ImportedCompanyEntityType.OTHER]: COMPANY_ASSET_TYPE_VALUES.private.other,
    };
    const privateOptions: SearchFilterCheckboxOption[] = Object.values(ImportedCompanyEntityType).map((entityType) => ({
        icon: privateOptionIcons[entityType],
        label: entityType,
        value: privateOptionValues[entityType],
    }));
    const supportedValues = new Set<string>(COMPANY_ASSET_TYPE_OPTIONS.map(({ value: optionValue }) => optionValue));
    const handleAssetTypeChange = (nextValue: string[]) =>
        handleChange(
            ['assetClasses'],
            nextValue.filter((selectedValue) => supportedValues.has(selectedValue)),
        );

    return (
        <div>
            <small style={{ color: token.colorTextTertiary }}>Public</small>
            <SearchFilterCheckboxList onChange={handleAssetTypeChange} options={publicOptions} value={value} />
            <Divider style={{ margin: '0.5rem 0' }} />
            <small style={{ color: token.colorTextTertiary }}>Private</small>
            <SearchFilterCheckboxList onChange={handleAssetTypeChange} options={privateOptions} value={value} />
        </div>
    );
};

export default AssetClass;
